/* import __COLOCATED_TEMPLATE__ from './outbound-campaign-list.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import ajax from 'embercom/lib/ajax';
import { buildVoiceLabel } from 'embercom/objects/ai-agent/fin-voice/fin-voice-label-utils';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';

export interface Campaign {
  id: number;
  name: string | null;
  workspace_phone_number_id: number | null;
  procedure_id: string | null;
  handover_configuration: FinVoiceNumberConfigurationHandoverConfiguration;
  voice: string | null;
  voicemail_enabled: boolean;
  voicemail_message: string | null;
  quiet_hours_enabled: boolean;
  calling_window_start: string;
  calling_window_end: string;
  status: string;
  total_rows: number;
  has_csv: boolean;
  created_at: string;
  stats?: {
    called: number;
    answered: number;
    unanswered: number;
    failed: number;
    answer_rate: number;
    resolved_percentage: number;
    completion_percentage: number;
  };
  archived: boolean;
  archivable: boolean;
  unarchivable: boolean;
}

interface OutboundCampaignListSignature {
  Args: {
    phoneNumbers: Record<string, PhoneNumber>;
    openNewCampaignRequested?: boolean;
    onNewCampaignRequestHandled?: () => void;
  };
}

export default class OutboundCampaignList extends Component<OutboundCampaignListSignature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked campaigns: Campaign[] = [];
  @tracked isLoading = true;
  @tracked hasLoadError = false;
  @tracked editingCampaign: Campaign | null = null;
  @tracked showEditPanel = false;
  @tracked currentPage = 1;
  @tracked hasMore = false;
  @tracked isLoadingMore = false;
  @tracked showArchivedSection = false;

  constructor(owner: unknown, args: OutboundCampaignListSignature['Args']) {
    super(owner, args);
    this.finVoiceConfigService.loadAllData();
    this.loadCampaigns();
  }

  get hasCampaigns() {
    return this.activeCampaigns.length > 0 || this.archivedCampaigns.length > 0;
  }

  get activeCampaigns() {
    return this.campaigns.filter((c) => !c.archived);
  }

  get archivedCampaigns() {
    return this.campaigns.filter((c) => c.archived);
  }

  get hasArchivedCampaigns() {
    return this.archivedCampaigns.length > 0;
  }

  @action
  async loadCampaigns() {
    this.isLoading = true;
    this.hasLoadError = false;
    this.currentPage = 1;
    try {
      let response: {
        campaigns: Campaign[];
        pagination: { has_more: boolean; total_count: number };
      } = await ajax({
        type: 'GET',
        url: '/ember/fin_voice/outbound_campaigns',
        data: { app_id: this.appService.app.id, page: 1 },
      });
      this.campaigns = response.campaigns;
      this.hasMore = response.pagination?.has_more ?? false;
    } catch (e: unknown) {
      this.campaigns = [];
      this.hasLoadError = true;
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.load-error'),
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async loadMoreCampaigns() {
    this.isLoadingMore = true;
    try {
      let nextPage = this.currentPage + 1;
      let response: {
        campaigns: Campaign[];
        pagination: { has_more: boolean; total_count: number };
      } = await ajax({
        type: 'GET',
        url: '/ember/fin_voice/outbound_campaigns',
        data: { app_id: this.appService.app.id, page: nextPage },
      });
      this.campaigns = [...this.campaigns, ...response.campaigns];
      this.currentPage = nextPage;
      this.hasMore = response.pagination?.has_more ?? false;
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.load-error'),
      );
    } finally {
      this.isLoadingMore = false;
    }
  }

  @action
  openNewCampaign() {
    this.editingCampaign = null;
    this.showEditPanel = true;
  }

  @action
  editCampaign(campaign: Campaign) {
    this.editingCampaign = campaign;
    this.showEditPanel = true;
  }

  @action
  onCampaignCardKeydown(campaign: Campaign, event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.editCampaign(campaign);
    }
  }

  @action
  closeEditPanel() {
    this.showEditPanel = false;
    this.editingCampaign = null;
    this.args.onNewCampaignRequestHandled?.();
  }

  @action
  toggleArchivedSection() {
    this.showArchivedSection = !this.showArchivedSection;
  }

  @action
  async onCampaignSaved() {
    this.closeEditPanel();
    await this.loadCampaigns();
  }

  @action
  statusLabel(status: string): string {
    switch (status) {
      case 'draft':
        return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.status-draft');
      case 'live':
        return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.status-active');
      case 'completed':
        return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.status-completed');
      case 'failed':
        return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.status-failed');
      default:
        return status;
    }
  }

  @action
  statusColor(status: string): 'gray' | 'green' | 'sky' | 'red' {
    switch (status) {
      case 'live':
        return 'green';
      case 'completed':
        return 'sky';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  }

  @action
  getPhoneNumberDisplay(campaign: Campaign): string {
    if (!campaign.workspace_phone_number_id) {
      return '';
    }
    let phoneNumber = this.args.phoneNumbers[String(campaign.workspace_phone_number_id)];
    if (!phoneNumber) {
      return '';
    }
    return phoneNumber.formattedPhoneNumber || phoneNumber.phoneNumber;
  }

  @action
  getVoiceLabel(campaign: Campaign): string {
    if (!campaign.workspace_phone_number_id) {
      return '';
    }
    let phoneNumberId = String(campaign.workspace_phone_number_id);
    let phoneNumber = this.finVoiceConfigService.phoneNumbers[phoneNumberId];
    let numberConfiguration = this.finVoiceConfigService.numberConfigurations[phoneNumberId];

    return buildVoiceLabel(
      phoneNumber,
      numberConfiguration,
      this.finVoiceConfigService.numberConfigurations,
      this.intl,
      campaign.voice,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::OutboundCampaignList': typeof OutboundCampaignList;
    'ai-agent/deploy/voice/outbound-campaign-list': typeof OutboundCampaignList;
  }
}
